/**
 * GlobalAudioManager - global sound playback functionality without requiring sound components.
 * Manages audio buffers and provides methods to play sounds at specific positions or globally.
 */

import * as THREE from 'three';

import { AssetPath } from '../storage/storageProvider.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';

import { AudioBus } from './AudioBus.js';
import { resourceManager } from './ResourceManager.js';
import { shortUUID } from './uuid.js';

export interface GlobalSoundOptions {
  /** Volume of the sound (0.0 to 1.0) */
  volume?: number;
  /** Whether the sound should loop */
  loop?: boolean;
  /** Maximum distance for positional audio */
  maxDistance?: number;
  /** Distance model for positional audio */
  distanceModel?: 'linear' | 'inverse' | 'exponential';
  /** Rolloff factor for positional audio */
  rolloffFactor?: number;
  /** Audio bus to play this sound on (defaults to 'SFX') */
  bus?: string;
}

export interface SoundHandle {
  /** Unique identifier for the sound instance */
  id: string;
  /** The URL of the audio file */
  audioUrl: string;
  /** Whether this is a positional or global sound */
  isPositional: boolean;
}

interface PlayingSoundInfo {
  handle: SoundHandle;
  audioObject: THREE.Audio | THREE.PositionalAudio;
  cleanup: () => void;
}

export class GlobalAudioManager {
  private static instance: GlobalAudioManager;
  private world: any = null;
  private playingSounds: Map<string, PlayingSoundInfo> = new Map();
  private buses: Map<string, AudioBus> = new Map();
  private audioContext: AudioContext | null = null;
  private isInitialized: boolean = false;

  private constructor() {
    ObjectTracker.track(this);
  }

  /**
   * Get the singleton instance of GlobalAudioManager
   */
  static getInstance(): GlobalAudioManager {
    if (!GlobalAudioManager.instance) {
      GlobalAudioManager.instance = new GlobalAudioManager();
    }
    return GlobalAudioManager.instance;
  }

  /**
   * Set the world reference for audio listener access.
   *
   * When the world changes or is cleared (`null`), stops playback, tears down buses, and resets
   * init state so the next world can re-bind to a new `AudioListener` / `AudioContext`. Without
   * this, the singleton would keep the previous `World` (and e.g. Manifold WASM) reachable across
   * editor play cycles.
   *
   * @param world - The world instance, or `null` when the world is destroyed.
   */
  setWorld(world: any): void {
    if (this.world === world) {
      return;
    }

    this.endPlay();

    if (this.buses.size > 0 || this.isInitialized) {
      this.teardownBuses();
    }

    this.world = world;
  }

  private teardownBuses(): void {
    for (const bus of this.buses.values()) {
      try {
        bus.disconnect();
      } catch {
        // ignore double-disconnect
      }
    }
    this.buses.clear();
    this.audioContext = null;
    this.isInitialized = false;
  }


  /**
   * Initialize audio buses if we have an audio listener
   */
  private ensureInitialized(): void {
    if (this.isInitialized) return;
    if (!this.world?.audioListener) return;

    const listener = this.world.audioListener;
    this.audioContext = listener.context;

    if (!this.audioContext) return;

    // Create default buses (they will auto-connect to Master by default)
    this.createBus('Master');
    this.createBus('SFX');
    this.createBus('Music');
    this.createBus('Voice');
    this.createBus('Ambience');

    // Connect Master bus to listener input
    const masterBus = this.buses.get('Master');
    if (masterBus) {
      masterBus.connect(listener.getInput());
    }

    this.isInitialized = true;
    console.log('[GlobalAudioManager] Audio buses initialized');
  }

  /**
   * Create a new audio bus.
   *
   * @param name - Name of the bus
   * @param parentBusName - Optional name of the parent bus to connect to (defaults to 'Master' if not specified, unless it IS 'Master')
   */
  public createBus(name: string, parentBusName?: string): AudioBus {
    if (!this.audioContext) {
      throw new Error('AudioContext not available');
    }
    const bus = new AudioBus(name, this.audioContext);
    this.buses.set(name, bus);

    // Connect to parent bus
    if (name !== 'Master') {
      const parentName = parentBusName ?? 'Master';
      const parentBus = this.buses.get(parentName);
      if (parentBus) {
        bus.connect(parentBus);
      } else {
        console.warn(`[GlobalAudioManager] Parent bus '${parentName}' not found for '${name}'. Bus will not be outputting audio until connected.`);
      }
    }

    return bus;
  }

  /**
   * Add a new audio bus.
   *
   * @param name - Name of the bus
   * @param parentBusName - Optional name of the parent bus to connect to
   */
  public addBus(name: string, parentBusName?: string): AudioBus {
    this.ensureInitialized();
    if (this.buses.has(name)) {
      return this.buses.get(name)!;
    }
    return this.createBus(name, parentBusName);
  }

  /**
   * Get an audio bus by name.
   *
   * @param name - Name of the bus
   */
  public getBus(name: string): AudioBus | undefined {
    this.ensureInitialized();
    return this.buses.get(name);
  }

  /**
   * Begin play lifecycle method
   */
  public beginPlay(): void {
    this.ensureInitialized();
    console.log('[GlobalAudioManager] Begin play - audio system ready');
  }

  /**
   * End play lifecycle method - stops all playing audio
   */
  public endPlay(): void {
    this.stopAllSounds();
    console.log('[GlobalAudioManager] End play - all audio stopped');
  }

  /**
   * Load a sound file and cache it for later use.
   *
   * @param key - Unique identifier for the sound (used as the audio URL)
   * @param audioUrl - URL to the audio file
   * @returns Promise that resolves when the sound is loaded
   */
  async loadSound(key: string, audioUrl: string): Promise<void> {
    try {
      console.log(`[GlobalAudioManager] Loading sound "${key}" from ${audioUrl}`);
      await resourceManager.loadSound(AssetPath.fromString(audioUrl));
      console.log(`[GlobalAudioManager] Successfully loaded sound "${key}"`);
    } catch (error) {
      console.error(`[GlobalAudioManager] Failed to load sound "${key}":`, error);
      throw error;
    }
  }

  /**
   * Check if a sound is loaded.
   *
   * @param audioUrl - The sound URL to check
   * @returns True if the sound is loaded
   */
  isSoundLoaded(audioUrl: string): boolean {
    const cachedSounds = resourceManager.getCachedSounds();
    return audioUrl in cachedSounds;
  }

  /**
   * Play a sound at a specific position in 3D space.
   *
   * @param audioUrl - URL to the audio file
   * @param position - The position in world space
   * @param options - Sound playback options
   * @returns Sound handle or null if failed to play
   */
  async playSoundAtPosition(audioUrl: string, position: THREE.Vector3, options: GlobalSoundOptions = {}): Promise<SoundHandle | null> {
    this.ensureInitialized();
    if (!this.world?.audioListener) {
      console.warn('[GlobalAudioManager] No audio listener available');
      return null;
    }

    try {
      const buffer = await resourceManager.loadSound(AssetPath.fromString(audioUrl));
      if (!buffer) {
        console.error(`[GlobalAudioManager] Failed to load sound buffer for ${audioUrl}`);
        return null;
      }

      // Determine the target listener (real or bus proxy)
      let targetListener = this.world.audioListener;
      const busName = options.bus ?? 'SFX';
      const bus = this.getBus(busName);

      if (bus) {
        // Create a proxy listener that routes input to the bus
        targetListener = new Proxy(this.world.audioListener, {
          get: (target, prop) => {
            if (prop === 'getInput') {
              return () => bus.getInput();
            }
            return (target as any)[prop];
          }
        });
      }

      // Create a positional audio object using the target listener
      const sound = new THREE.PositionalAudio(targetListener);
      sound.setBuffer(buffer);
      sound.setVolume(options.volume ?? 1.0);
      sound.setLoop(options.loop ?? false);
      sound.position.set(position.x, position.y, position.z);

      // Configure positional audio properties
      if (options.maxDistance !== undefined) {
        sound.setMaxDistance(options.maxDistance);
      }
      if (options.distanceModel !== undefined) {
        sound.setDistanceModel(options.distanceModel);
      }
      if (options.rolloffFactor !== undefined) {
        sound.setRolloffFactor(options.rolloffFactor);
      }

      // Add to world scene
      this.world.scene.add(sound);

      // Create handle and track the sound
      const handle: SoundHandle = {
        id: shortUUID(),
        audioUrl,
        isPositional: true
      };

      const cleanup = () => {
        this.world.scene.remove(sound);
        this.playingSounds.delete(handle.id);
        // console.log(`[GlobalAudioManager] Positional sound ${handle.id} cleaned up`);
      };

      this.playingSounds.set(handle.id, {
        handle,
        audioObject: sound,
        cleanup
      });

      // Play the sound
      sound.play();

      // Clean up when the sound ends (if not looping)
      if (!options.loop) {
        sound.onEnded = cleanup;
      }

      // console.log(`[GlobalAudioManager] Playing positional sound ${handle.id} at position`, position);
      return handle;
    } catch (error) {
      console.error('[GlobalAudioManager] Failed to play positional sound:', error);
      return null;
    }
  }

  /**
   * Play a global sound (non-positional).
   *
   * @param audioUrl - URL to the audio file
   * @param options - Sound playback options
   * @returns Sound handle or null if failed to play
   */
  async playGlobalSound(audioUrl: string, options: GlobalSoundOptions = {}): Promise<SoundHandle | null> {
    this.ensureInitialized();
    if (!this.world?.audioListener) {
      console.warn('[GlobalAudioManager] No audio listener available');
      return null;
    }


    try {
      const buffer = await resourceManager.loadSound(AssetPath.fromString(audioUrl));
      if (!buffer) {
        console.error(`[GlobalAudioManager] Failed to load sound buffer for ${audioUrl}`);
        return null;
      }

      // Determine the target listener (real or bus proxy)
      let targetListener = this.world.audioListener;
      const busName = options.bus ?? 'SFX';
      const bus = this.getBus(busName);

      if (bus) {
        // Create a proxy listener that routes input to the bus
        targetListener = new Proxy(this.world.audioListener, {
          get: (target, prop) => {
            if (prop === 'getInput') {
              return () => bus.getInput();
            }
            return (target as any)[prop];
          }
        });
      }

      // Create a global audio object
      const sound = new THREE.Audio(targetListener);
      sound.setBuffer(buffer);
      sound.setVolume(options.volume ?? 1.0);
      sound.setLoop(options.loop ?? false);

      // Create handle and track the sound
      const handle: SoundHandle = {
        id: shortUUID(),
        audioUrl,
        isPositional: false
      };

      const cleanup = () => {
        this.playingSounds.delete(handle.id);
        // console.log(`[GlobalAudioManager] Global sound ${handle.id} cleaned up`);
      };

      this.playingSounds.set(handle.id, {
        handle,
        audioObject: sound,
        cleanup
      });

      // Play the sound
      sound.play();

      // Clean up when the sound ends (if not looping)
      if (!options.loop) {
        sound.onEnded = cleanup;
      }

      // console.log(`[GlobalAudioManager] Playing global sound ${handle.id}`);
      return handle;
    } catch (error) {
      console.error('[GlobalAudioManager] Failed to play global sound:', error);
      return null;
    }
  }

  /**
   * Stop a playing sound by its handle.
   *
   * @param handle - The sound handle returned from a play method
   * @returns True if the sound was stopped successfully
   */
  public stopSound(handle: SoundHandle): boolean {
    const soundInfo = this.playingSounds.get(handle.id);
    if (!soundInfo) {
      // console.warn(`[GlobalAudioManager] Sound ${handle.id} not found or already stopped`);
      return false;
    }

    try {
      // Stop the audio
      if (soundInfo.audioObject.isPlaying) {
        soundInfo.audioObject.stop();
      }

      // Clean up resources
      soundInfo.cleanup();

      // console.log(`[GlobalAudioManager] Stopped sound ${handle.id}`);
      return true;
    } catch (error) {
      console.error(`[GlobalAudioManager] Failed to stop sound ${handle.id}:`, error);
      return false;
    }
  }

  /** Stop all currently playing sounds. */
  public stopAllSounds(): void {
    console.log(`[GlobalAudioManager] Stopping ${this.playingSounds.size} playing sounds`);

    for (const [id, soundInfo] of this.playingSounds.entries()) {
      try {
        if (soundInfo.audioObject.isPlaying) {
          soundInfo.audioObject.stop();
        }
        soundInfo.cleanup();
      } catch (error) {
        console.error(`[GlobalAudioManager] Failed to stop sound ${id}:`, error);
      }
    }

    this.playingSounds.clear();
  }

  /**
   * Check if a sound is currently playing.
   *
   * @param handle - The sound handle to check
   * @returns True if the sound is playing
   */
  public isSoundPlaying(handle: SoundHandle): boolean {
    const soundInfo = this.playingSounds.get(handle.id);
    return soundInfo?.audioObject.isPlaying ?? false;
  }

  /**
   * Get the number of currently playing sounds.
   *
   * @returns Number of playing sounds
   */
  public getPlayingSoundCount(): number {
    return this.playingSounds.size;
  }

  /**
   * Get all currently playing sound handles.
   *
   * @returns Array of sound handles
   */
  public getPlayingSounds(): SoundHandle[] {
    return Array.from(this.playingSounds.values()).map(info => info.handle);
  }

  /**
   * Synchronous version of playSoundAtPosition (for convenience).
   * Note: This will still load asynchronously if the sound isn't cached.
   *
   * @param audioUrl - URL to the audio file
   * @param position - The position in world space
   * @param options - Sound playback options
   * @returns Promise that resolves to a sound handle or null if failed
   */
  playSoundAtPositionSync(audioUrl: string, position: THREE.Vector3, options: GlobalSoundOptions = {}): Promise<SoundHandle | null> {
    return this.playSoundAtPosition(audioUrl, position, options);
  }

  /**
   * Synchronous version of playGlobalSound (for convenience).
   * Note: This will still load asynchronously if the sound isn't cached.
   *
   * @param audioUrl - URL to the audio file
   * @param options - Sound playback options
   * @returns Promise that resolves to a sound handle or null if failed
   */
  playGlobalSoundSync(audioUrl: string, options: GlobalSoundOptions = {}): Promise<SoundHandle | null> {
    return this.playGlobalSound(audioUrl, options);
  }

  /**
   * Unload a sound from memory.
   *
   * @param audioUrl - The sound URL to unload
   */
  unloadSound(audioUrl: string): void {
    // ResourceManager handles its own cache management
    console.log(`[GlobalAudioManager] Sound unloading is managed by ResourceManager for "${audioUrl}"`);
  }

  /** Unload all sounds from memory. */
  unloadAllSounds(): void {
    // ResourceManager handles its own cache management
    resourceManager.clearCachedResources();
    console.log('[GlobalAudioManager] All resources cleared via ResourceManager');
  }

  /**
   * Get a list of all loaded sound URLs.
   *
   * @returns Array of loaded sound URLs
   */
  getLoadedSounds(): string[] {
    return Object.keys(resourceManager.getCachedSounds());
  }
}